// Typed API client — framework-agnostic (no React import).
//
// Auth model: the browser sends the same-origin httpOnly Flask session cookie
// automatically (credentials: "include"). We do NOT store tokens in JS.
//
// CSRF: Flask-WTF validates X-CSRFToken on mutating requests. The Jinja app
// injected this via a window.fetch monkey-patch; the SPA can't rely on that, so
// we fetch the token once from GET /api/v1/csrf-token and attach it ourselves.

import { applyBranding, hasBranding } from "./branding";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

let csrfToken: string | null = null;

export async function ensureCsrf(): Promise<string> {
  if (csrfToken) return csrfToken;
  const res = await fetch("/api/v1/csrf-token", { credentials: "include" });
  if (!res.ok) throw new ApiError(res.status, "Failed to obtain CSRF token");
  const data = (await res.json()) as { token: string };
  csrfToken = data.token;
  return csrfToken;
}

type Json = Record<string, unknown> | unknown[];

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: Json | FormData;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? "GET";
  const headers: Record<string, string> = {};
  const isMutation = method !== "GET";

  if (isMutation) headers["X-CSRFToken"] = await ensureCsrf();

  let body: BodyInit | undefined;
  if (opts.body instanceof FormData) {
    body = opts.body;
  } else if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.body);
  }

  const res = await fetch(`/api/v1${path}`, {
    method,
    headers,
    body,
    credentials: "include",
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const data = text ? JSON.parse(text) : undefined;

  if (!res.ok) {
    // A stale CSRF token (e.g. after session refresh) → clear and let caller retry.
    if (res.status === 400 && isMutation) csrfToken = null;
    // F22: a suspended recruiter gets sent to the login page, which says why,
    // instead of each panel showing "Error: Authentication required."
    if (res.status === 403 && (data as { code?: string })?.code === "account_suspended") {
      window.location.href = "/auth/login?error=suspended";
    }
    throw new ApiError(res.status, (data as { error?: string })?.error ?? res.statusText, data);
  }

  // Apply white-label branding wherever it arrives, rather than asking every
  // page to remember. Several candidate screens (Home, Briefs, KB, Settings)
  // read the logo out of their own payload's `branding` block but never injected
  // its `cssVars`, so the portal kept house-plum buttons on a rebranded agency.
  // The chokepoint is the only place that can't be forgotten by the next screen.
  if (hasBranding(data)) applyBranding(data.branding);

  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: Json | FormData) => request<T>(path, { method: "POST", body }),
  patch: <T>(path: string, body?: Json) => request<T>(path, { method: "PATCH", body }),
  delete: <T>(path: string, body?: Json) => request<T>(path, { method: "DELETE", body }),
};
